/**
 * 清新薄荷模版 - 列表页（v4）
 * 
 * 设计核心理念：与红色模版（左侧栏+数据表格）完全不同
 * - 顶部：分类标签横向筛选栏（非左侧边栏）
 * - 内容区：响应式卡片网格（非数据表格）
 * - 搜索框内嵌在筛选栏
 * - 更现代的视觉风格，信息密度适中
 */

import { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/templates/teal/components/Navbar";
import Footer from "@/templates/teal/components/Footer";
import {
  getSiteCategories,
  getListings,
  getNavCategories,
  type FrontendCategory,
  type ListingItem,
} from "@/utils/api";
import { useSite } from "@/context/SiteContext";

const SORT_OPTIONS = [
  { key: "latest", label: "最新", icon: "ri-time-line" },
  { key: "popular", label: "最热", icon: "ri-fire-line" },
  { key: "alpha", label: "名称", icon: "ri-sort-alphabet-asc" },
] as const;

const ITEMS_PER_ROW = { sm: 2, lg: 3, xl: 4 };

export default function TealCategoryPage() {
  const { slug } = useParams<{ slug?: string }>();
  const { siteId, loading: siteLoading } = useSite();

  const [categories, setCategories] = useState<FrontendCategory[]>([]);
  const [navCategories, setNavCategories] = useState<Array<{ id: string; name: string; slug: string; icon: string }>>([]);
  const [listings, setListings] = useState<ListingItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [activeCategoryId, setActiveCategoryId] = useState<string>("");
  const [sortBy, setSortBy] = useState("latest");
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 24;

  useEffect(() => {
    if (siteLoading || !siteId) return;
    Promise.all([
      getSiteCategories().then(setCategories).catch(() => {}),
      getNavCategories().then(setNavCategories).catch(() => {}),
    ]);
  }, [siteId, siteLoading]);

  useEffect(() => {
    if (slug && categories.length > 0) {
      const found = categories.find((c) => c.slug === slug || c.id === slug);
      if (found) setActiveCategoryId(found.id);
    }
  }, [slug, categories]);

  useEffect(() => {
    if (siteLoading || !siteId) return;
    setLoading(true);
    const params: Record<string, any> = { page: currentPage, page_size: PAGE_SIZE };
    if (activeCategoryId) params.category_id = activeCategoryId;
    getListings(params)
      .then((data) => {
        setListings(data.list || []);
        setTotal(data.total || 0);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [siteId, siteLoading, activeCategoryId, currentPage]);

  const currentCategoryName = useMemo(() => {
    if (activeCategoryId) {
      const cat = categories.find((c) => c.id === activeCategoryId);
      return cat?.name || "全部分类";
    }
    return "全部分类";
  }, [activeCategoryId, categories]);

  const sortedListings = useMemo(() => {
    const list = [...listings];
    if (sortBy === "popular") list.sort((a, b) => (b.view_count || 0) - (a.view_count || 0));
    if (sortBy === "alpha") list.sort((a, b) => a.title.localeCompare(b.title, "zh"));
    return list;
  }, [listings, sortBy]);

  const displayCategories = navCategories.length > 0 ? navCategories : categories;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="min-h-screen bg-[#f8fafb]">
      <Navbar />

      {/* ── 顶部：面包屑 + 分类标签筛选栏（关键差异：横排标签替代左侧边栏）── */}
      <div className="bg-white border-b border-gray-100 pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* 面包屑 */}
          <nav className="flex items-center gap-1.5 text-sm py-3">
            <Link to="/" className="text-gray-400 hover:text-teal-600 transition-colors flex items-center gap-1">
              <i className="ri-home-4-line" /> 首页
            </Link>
            <i className="ri-arrow-right-s-line text-xs text-gray-300" />
            {activeCategoryId ? (
              <>
                <button onClick={() => { setActiveCategoryId(""); setCurrentPage(1); }}
                  className="text-gray-400 hover:text-teal-600 transition-colors cursor-pointer">
                  全部分类
                </button>
                <i className="ri-arrow-right-s-line text-xs text-gray-300" />
                <span className="text-teal-600 font-semibold">{currentCategoryName}</span>
              </>
            ) : (
              <span className="text-teal-600 font-semibold">全部分类</span>
            )}
          </nav>

          {/* 分类标签横排 */}
          <div className="pb-3 overflow-x-auto scrollbar-hide">
            <div className="flex gap-1.5 min-w-max">
              <button
                onClick={() => { setActiveCategoryId(""); setCurrentPage(1); }}
                className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer ${
                  !activeCategoryId
                    ? "bg-teal-600 text-white shadow-md shadow-teal-500/20"
                    : "bg-gray-100 text-gray-600 hover:bg-teal-50 hover:text-teal-600"
                }`}
              >
                全部
              </button>
              {displayCategories.slice(0, 20).map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => {
                    setActiveCategoryId(cat.id === activeCategoryId ? "" : cat.id);
                    setCurrentPage(1);
                  }}
                  className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer ${
                    activeCategoryId === cat.id
                      ? "bg-teal-600 text-white shadow-md shadow-teal-500/20"
                      : "bg-gray-100 text-gray-600 hover:bg-teal-50 hover:text-teal-600"
                  }`}
                >
                  {cat.icon && <i className={`${cat.icon} mr-1.5 text-xs`} />}
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── 工具栏 ── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-800">{currentCategoryName}</h2>
          <div className="flex items-center gap-1">
            {SORT_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                onClick={() => setSortBy(opt.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer ${
                  sortBy === opt.key
                    ? "bg-teal-600 text-white shadow-sm"
                    : "bg-gray-100 text-gray-500 hover:text-teal-600 hover:bg-teal-50"
                }`}
              >
                <i className={`${opt.icon} text-[11px]`} />
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── 卡片网格（核心差异：替代表格）── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="animate-pulse bg-white rounded-xl border border-gray-100 overflow-hidden">
                <div className="aspect-[4/3] bg-gray-200" />
                <div className="p-4 space-y-2">
                  <div className="w-3/4 h-5 bg-gray-200 rounded" />
                  <div className="w-1/2 h-4 bg-gray-100 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : sortedListings.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {sortedListings.map((listing) => (
              <Link
                key={listing.id}
                to={`/listing/${listing.id}`}
                className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-lg hover:border-teal-200 transition-all duration-300 group flex flex-col"
              >
                <div className="aspect-[4/3] bg-gray-50 overflow-hidden flex items-center justify-center relative">
                  {listing.images?.[0] ? (
                    <img src={listing.images[0]} alt={listing.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  ) : (
                    <div className="flex flex-col items-center gap-1">
                      <i className="ri-image-line text-gray-300 text-3xl" />
                      <span className="text-[10px] text-gray-300">暂无图片</span>
                    </div>
                  )}
                  {listing.is_pinned && (
                    <span className="absolute top-2 left-2 text-[10px] px-2 py-0.5 rounded-full bg-amber-500 text-white font-bold shadow-sm">
                      顶
                    </span>
                  )}
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <h3 className="text-[15px] font-semibold text-gray-800 line-clamp-2 leading-snug group-hover:text-teal-600 transition-colors mb-auto">
                    {listing.title}
                  </h3>
                  <p className="text-xs text-gray-400 mt-2 line-clamp-1">{listing.content?.substring(0, 60) || "暂无描述"}</p>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-50">
                    <span className="text-xs px-2 py-0.5 bg-teal-50 text-teal-600 rounded-full font-medium">
                      {listing.category?.name || "未分类"}
                    </span>
                    <span className="text-[11px] text-gray-400 flex items-center gap-1">
                      {sortBy === "popular" ? (
                        <><i className="ri-eye-line text-[10px]" />{listing.view_count}</>
                      ) : (
                        <><i className="ri-time-line text-[10px]" />{formatDate(listing.created_at)}</>
                      )}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-teal-50">
              <i className="ri-inbox-line text-2xl text-teal-400" />
            </div>
            <p className="text-gray-500 font-medium">该分类暂无信息</p>
            <p className="text-gray-400 text-sm mt-1">换个分类试试，或返回首页</p>
            <Link to="/" className="inline-flex items-center gap-1.5 mt-4 px-5 py-2.5 bg-teal-600 text-white text-sm rounded-xl hover:bg-teal-700 transition-colors">
              <i className="ri-arrow-left-line" />返回首页
            </Link>
          </div>
        )}

        {/* 分页 */}
        {!loading && totalPages > 1 && (
          <div className="mt-8 flex items-center justify-center gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="w-10 h-10 flex items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 hover:border-teal-300 hover:text-teal-600 disabled:opacity-30 transition-all cursor-pointer"
            >
              <i className="ri-arrow-left-s-line" />
            </button>

            {generatePageNumbers(currentPage, totalPages).map((p, i) =>
              typeof p === "number" ? (
                <button
                  key={i}
                  onClick={() => setCurrentPage(p)}
                  className={`w-10 h-10 flex items-center justify-center rounded-xl text-sm font-medium transition-all cursor-pointer ${
                    currentPage === p
                      ? "bg-teal-600 text-white shadow-md shadow-teal-500/20"
                      : "border border-gray-200 bg-white text-gray-600 hover:border-teal-300 hover:text-teal-600"
                  }`}
                >
                  {p}
                </button>
              ) : (
                <span key={i} className="w-10 h-10 flex items-center justify-center text-gray-400 text-xs">...</span>
              )
            )}

            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="w-10 h-10 flex items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 hover:border-teal-300 hover:text-teal-600 disabled:opacity-30 transition-all cursor-pointer"
            >
              <i className="ri-arrow-right-s-line" />
            </button>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}分钟前`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}小时前`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}天前`;
  return `${d.getMonth() + 1}-${d.getDate()}`;
}

function generatePageNumbers(current: number, total: number): (number | string)[] {
  const pages: (number | string)[] = [];
  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
  } else {
    pages.push(1);
    if (current > 3) pages.push("...");
    for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
      pages.push(i);
    }
    if (current < total - 2) pages.push("...");
    pages.push(total);
  }
  return pages;
}
